import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "configuring-webhooks-for-created-cloud-libraries-events"
    }}>{`Configuring Webhooks for Created Cloud Libraries Events`}</h1>
    <p>{`I/O Events makes it possible to listen for changes to Creative Cloud Libraries and get a notification when those changes occur. Developers can subscribe to `}<em parentName="p">{`create`}</em>{`, `}<em parentName="p">{`delete`}</em>{`, and `}<em parentName="p">{`update`}</em>{` events for a user's Creative Cloud Libraries. This tutorial will walk through how to set up and configure your project to listen for Creative Cloud Libraries events.`}</p>
    <h2 {...{
      "id": "creative-cloud-library-event-type-descriptions"
    }}>{`Creative Cloud Library Event Type Descriptions`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Event Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Creative Cloud Library Created`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Triggers whenever a user creates a new Library including adding a public library to "Your Work"`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Creative Cloud Library Deleted`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Triggers when a user deletes a Library`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Creative Cloud Library Updated`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Triggers whenever there is a change to a Library including an element being added/deleted/updated or metadata of a Library being changed`}</td>
        </tr>
      </tbody>
    </table>
    <p><em parentName="p">{`Note: Currently there is no way to listen for element-level changes only on a Library. This is on the roadmap.`}</em></p>
    <h2 {...{
      "id": "before-you-start"
    }}>{`Before you start`}</h2>
    <p>{`Subscribing to Adobe I/O Events requires a URL that can be publicly accessible by the Events service. That means the URL provided for the Webhook URL in the Adobe Developer Console needs to be accessible via the internet. For doing local testing, this tutorial uses `}<a parentName="p" {...{
        "href": "https://ngrok.com/"
      }}>{`ngrok`}</a>{` which provides a publicly addressable URL to a locally running service.`}</p>
    <h3 {...{
      "id": "technology-used"
    }}>{`Technology Used`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://ngrok.com/"
        }}>{`ngrok`}</a>{` which requires a free account`}</li>
      <li parentName="ul">{`Node and NPM to run the `}<a parentName="li" {...{
          "href": "https://github.com/adobeio/io-event-sample-webhook"
        }}>{`sample webhook project`}</a></li>
    </ul>
    <h3 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h3>
    <ul>
      <li parentName="ul">{`Clone the `}<a parentName="li" {...{
          "href": "https://github.com/adobeio/io-event-sample-webhook"
        }}>{`I/O Events Sample Webhook project`}</a>{` into a local directory`}</li>
      <li parentName="ul">{`Create an `}<a parentName="li" {...{
          "href": "https://ngrok.com/"
        }}>{`ngrok`}</a>{` account then `}<a parentName="li" {...{
          "href": "https://ngrok.com/download"
        }}>{`download and install ngrok`}</a></li>
    </ul>
    <h2 {...{
      "id": "development-steps"
    }}>{`Development Steps`}</h2>
    <h3 {...{
      "id": "1-preparing-the-webhook-urls"
    }}>{`1. Preparing the Webhook URLs`}</h3>
    <p>{`When creating an Event Registration, Adobe I/O Events will send a challenge to the given Webhook URL before sending it events. So a valid Webhook URL needs to be set up and defined before creating the registration. In production, this URL will be a publicly accessible URL that responds correctly to the challenge. This tutorial will be using ngrok.`}</p>
    <p>{`Open a terminal window to the location where the `}<a parentName="p" {...{
        "href": "https://github.com/adobeio/io-event-sample-webhook"
      }}>{`I/O Events Sample Webhook project`}</a>{` was cloned. In that directory, run`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`npm install
npm start
`}</code></pre>
    <p>{`This will create a local service that responds correctly to the I/O Events challenge and will show logging information about events received. Once running, it can be accessed at http://localhost:3000. Browse to that URL. In the webhook URI input box, put `}<inlineCode parentName="p">{`libraries`}</inlineCode>{` and press the "Connect" button. This will setup a webhook URL and will display logging information whenever an events call is made. The logging box should say `}<em parentName="p">{`Connected to: http://localhost:3000/webhook/libraries`}</em>{`.`}</p>
    <p>{`The next step is to make it available to the internet using ngrok. Open another terminal window in the location where ngrok was installed and run`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{` ./ngrok http 3000
`}</code></pre>
    <p>{`That should result in an output that looks like this`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`Session Status                online
Account                       <Your Name>
Version                       2.3.35
Region                        United States (us)
Web Interface                 http://127.0.0.1:4040
Forwarding                    http://a7ad21255b75.ngrok.io -> http://localhost:3000
Forwarding                    https://a7ad21255b75.ngrok.io -> http://localhost:3000
`}</code></pre>
    <p>{`Note the `}<inlineCode parentName="p">{`Forwarding`}</inlineCode>{` URLs as these will be used to set up the event registration in the next step.`}</p>
    <h3 {...{
      "id": "2-create-event-registrations"
    }}>{`2. Create Event Registrations`}</h3>
    <p>{`Creating the event registrations required for listening for Creative Cloud Libraries events happens in the Adobe Developer Console. Once logged in to the Developer Console, select the project currently used for your Creative Cloud Libraries integration.`}</p>
    <p>{`In the project, click "Add to Project" and select "Event". On the next screen, select "Creative Cloud Libraries" and push the Next button.`}</p>
    <p>{`The next screen shows the different event types that are available. Applications can listen for one or more event types from a single webhook URL. Select all three event types: Creative Cloud Library Deleted, Creative Cloud Library Updated, and Creative Cloud Library Created, then push Next.`}</p>
    <p>{`User consent is required to listen for events on their Libraries, so developers need to define OAuth credentials for users to explicitly authorize that consent. To do that, developers need to define a set of OAuth credentials by providing a redirect URI and redirect URI pattern. More information about the OAuth flow is available in `}<a parentName="p" {...{
        "href": "https://github.com/cc-libraries-api/api-docs/blob/master/tutorials/.how-to-get-access-tokens-with-oauth.md"
      }}>{`How to get an Access Token with OAuth`}</a>{`.`}</p>
    <p>{`If the integration has an existing set of OAuth credentials, this information should already be filled out. If not, correct URIs for this tutorial are:`}</p>
    <ul>
      <li parentName="ul">{`Redirect URI: `}<inlineCode parentName="li">{`https://adobeioruntime.net/api/v1/web/io-solutions/adobe-oauth-playground/cb`}</inlineCode></li>
      <li parentName="ul">{`Redirect URI Pattern: `}<inlineCode parentName="li">{`https://adobeioruntime\\\\.net`}</inlineCode></li>
    </ul>
    <p>{`Give the Event Registration a name and optionally a description. In the webhook URL field, enter the https line from the `}<inlineCode parentName="p">{`Forwarding`}</inlineCode>{` output in previous step follwed by `}<inlineCode parentName="p">{`/webhook/libraries`}</inlineCode>{`. It should look something like `}<inlineCode parentName="p">{`https://a7ad21255b75.ngrok.io/webhoook/libraries`}</inlineCode>{`. When finished, press "Save Configured Events".`}</p>
    <p>{`On the next screen, a successful setup will show the status of "Active" under Registration Details. The logging information in the Webhook app running on http://localhost:3000/ should show a single `}<inlineCode parentName="p">{`GET`}</inlineCode>{` request with a `}<inlineCode parentName="p">{`challenge`}</inlineCode>{` query param.`}</p>
    <h3 {...{
      "id": "3-authorizing-events"
    }}>{`3. Authorizing Events`}</h3>
    <p>{`As mentioned, in order to receive a user's events, the user needs to explicitly authorize an application to listen for events via an OAuth flow. The next step is to set up that authorization. This example will use the `}<a parentName="p" {...{
        "href": "https://adobeioruntime.net/api/v1/web/io-solutions/adobe-oauth-playground/oauth.html"
      }}>{`OAuth Playground`}</a>{` but any method of initiating the OAuth flow can be used.`}</p>
    <p><em parentName="p">{`Note: When using OAuth to call APIs, an access token is generated and that access token is passed in the header to successfully call the API. In the case of Events, the access token isn't used. I/O Events registers the authorization and Events will be sent to the application until the user revokes their consent.`}</em></p>
    <p>{`To initiate the consent workflow, open a new browser window and browse to `}<a parentName="p" {...{
        "href": "https://adobeioruntime.net/api/v1/web/io-solutions/adobe-oauth-playground/oauth.html"
      }}>{`https://adobeioruntime.net/api/v1/web/io-solutions/adobe-oauth-playground/oauth.html`}</a>{`. Find the integration's API key by selecting the "Credentials" item on the page confirming the Event Registration, copy the Client ID, and paste it in the API Key field of the OAuth Playground. Do the same for the client secret. In the "Scopes" field, add `}<inlineCode parentName="p">{`creative_sdk,profile,address,AdobeID,email,cc_files,cc_libraries`}</inlineCode>{` so that it reads `}<inlineCode parentName="p">{`openid,creative_sdk,profile,address,AdobeID,email,cc_files,cc_libraries`}</inlineCode>{` then press the "Generate Tokens" button.`}</p>
    <p>{`The OAuth Playground will show an Adobe login screen. Login with a valid username and password and then hit "Allow Access" to allow the application to listen for events from the authorized account.`}</p>
    <h3 {...{
      "id": "4-testing-and-verifying-events"
    }}>{`4. Testing and Verifying Events`}</h3>
    <p>{`Now the webhook has been configured successfully and an account has authorized the sending of events to the specified webhook URL. Any changes to Libraries in that account will trigger events.`}</p>
    <p>{`To test, browse to `}<a parentName="p" {...{
        "href": "https://assets.adobe.com/public/06cb8dc3-921f-4be6-64fa-f04de9b0a752"
      }}>{`https://assets.adobe.com/public/06cb8dc3-921f-4be6-64fa-f04de9b0a752`}</a>{` in the same browser that was used to initiate the OAuth flow. Once loaded, click the "Copy to your Work" button. This will trigger a `}<em parentName="p">{`create`}</em>{` event.`}</p>
    <p>{`The Webhook app (http://localhost:3000/) should now show a `}<inlineCode parentName="p">{`POST`}</inlineCode>{` request that includes a JSON `}<inlineCode parentName="p">{`body`}</inlineCode>{` with information about the event including what kind of event was triggered (`}<inlineCode parentName="p">{`event:action`}</inlineCode>{`), the id of the newly created Library (`}<inlineCode parentName="p">{`repo:id`}</inlineCode>{`), and the user who caused the event to trigger (`}<inlineCode parentName="p">{`repo:modifiedBy`}</inlineCode>{`).`}</p>
    <p>{`This information can also be seen in the Developer Console by selecting the "Debug Tracing" tab after selecting the event registration on the Project screen. (If the "Debug Tracing" section is greyed out, try refreshing the page). The Debug Tracing area provides the ability to filter by status code as well as information about how long the request took and the headers/payload that were sent with the event.`}</p>
    <p>{`To trigger an update event, rename the Library. Deleting the library will trigger a delete event.`}</p>
    <h2 {...{
      "id": "next-steps"
    }}>{`Next Steps`}</h2>
    <p>{`This tutorial provides a working local application to receive (and log) events. When re-implementing this in production, remember the need to respond to the challenge and get user authorization. More information can be found in the `}<a parentName="p" {...{
        "href": "https://www.adobe.io/apis/experienceplatform/events/docs.html#!adobedocs/adobeio-events/master/readme.md"
      }}>{`Adobe I/O Events documentation`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      